import {createI18n} from "vue-i18n";
import {inject} from "vue";
import moment from "moment";

class Locale {
    app;
    instance;
    emitter;
    lang;
    locale;
    localeKey;
    weekStart;
    timeFormat;
    dateFormat;
    timePeriod;
    rtlLoaded = false;
    locales = {};

    constructor(app, { messages, lang, locale, dir, week_start_day = 0, time_format, date_format  }) {
        this.locales = import.meta.glob(
            '/node_modules/moment/dist/locale/*.js'
        );
        this.app = app;
        this.emitter = app.config.globalProperties.$emitter;
        this.lang = lang;
        this.locale = locale;

        if (typeof week_start_day !== 'undefined') {
            this.weekStart = week_start_day;
            this.timeFormat = this.fixTimeFormat(time_format);
            this.dateFormat = this.fixDateFormat(date_format);
            this.setupMoment();
        }

        this.instance = createI18n({
            legacy: false,
            locale: lang,
            messages,
            globalInjection: true,
        });
        app.use(this.instance);

        this.loadStyles(dir);
    }

    set(data, emit) {
        if(data?.locale && data?.messages) {
            this.locale = data.locale;
            this.lang = data.lang;
            this.instance.global.messages.value[data.lang] = data.messages;
            this.instance.global.locale.value = data.lang;
            this.loadStyles(data.dir);
        }

        this.weekStart = data?.week_start_day || this.weekStart;
        this.timeFormat = this.fixTimeFormat(data?.time_format || this.timeFormat);
        this.dateFormat = this.fixDateFormat(data?.date_format || this.dateFormat);

        this.setupMoment().then(() => {
            if (emit) {
                this.emitter.emit('locale:change');
            }
        });
    }

    updateFromUser() {
        const store = this.app.config.globalProperties.$store;
        const user = store.getters['auth/user'];

        this.set({
            week_start_day: user.week_start_day,
            time_format: user.time_format,
            date_format: user.date_format,
        });
    }

    fixTimeFormat(format) {
        this.timePeriod = format.split(' ').length > 1;
        return format
            .replace('H', this.timePeriod > 1 ? 'H' : 'HH')
            .replace('i', 'mm')
            .replace('s', 'ss');
    }

    fixDateFormat(format) {
        return format.replace('d', 'DD')
            .replace('m', 'MM')
            .replace('Y', 'yyyy');
    }

    setupMoment() {
        return new Promise((resolve) => {
            this.importLocaleFile().then((data) => {
                const timeFormat = this.timeFormat.split(':');
                const shortTimeFormat = `${timeFormat[0]}:${timeFormat[1]}${this.timePeriod ? ' A' : ''}`;
                const custom = `${this.localeKey}-custom`;

                const baseConfig = Object.assign(data?.default?._config || {}, {
                    parentLocale: this.localeKey,
                    week: { dow: this.weekStart },
                    longDateFormat: {
                        LT: shortTimeFormat,
                        LTS: timeFormat.join(':'),
                        L: this.dateFormat,
                        LL: this.dateFormat,
                        LLL: `${this.dateFormat} ${shortTimeFormat}`,
                        LLLL: `${this.dateFormat}, ${shortTimeFormat}`
                    }
                });

                baseConfig.parentLocale = this.localeKey;
                baseConfig.preparse = this._toLatin;
                baseConfig.postformat = this._toLatin;

                moment.updateLocale(custom, baseConfig);
                moment.locale(custom);
                resolve();
            });
        });
    }

    _toLatin(str = '') {
        const map = {
            '٠':'0','١':'1','٢':'2','٣':'3','٤':'4',
            '٥':'5','٦':'6','٧':'7','٨':'8','٩':'9',

            '۰':'0','۱':'1','۲':'2','۳':'3','۴':'4',
            '۵':'5','۶':'6','۷':'7','۸':'8','۹':'9',

            '०':'0','१':'1','२':'2','३':'3','४':'4',
            '५':'5','६':'6','७':'7','८':'8','९':'9'
        };

        return str.replace(/[٠-٩۰-۹०-९]/g, d => map[d] || d);
    }

    importLocaleFile() {
        const keys = (this.locale || '').split(/[_-]/);
        let key = null;

        if (keys.length > 1) {
            this.localeKey = `${keys[0].toLowerCase()}-${keys[1].toLowerCase()}`;
            let key = `/node_modules/moment/dist/locale/${this.localeKey}.js`;

            if (typeof this.locales[key] === 'undefined') {
                this.localeKey = keys[0].toLowerCase();
                key = `/node_modules/moment/dist/locale/${this.localeKey}.js`;
            }
        } else {
            this.localeKey = keys[0].toLowerCase();
            key = `/node_modules/moment/dist/locale/${this.localeKey}.js`;
        }


        if (typeof this.locales[key] === 'undefined') {
            this.localeKey = 'en';
            return new Promise((resolve) => {
                resolve();
            });
        }
        return this.locales[key]();
    }

    async loadStyles(dir) {
        document.querySelector('html')
            .setAttribute('direction', dir);

        if (dir === 'rtl' && !this.rtlLoaded) {
            await import('@/css/rtl.scss');
            this.rtlLoaded = true;
        }
    }
}

export default {
    install(app, options) {
        const locale = new Locale(app, options);
        app.config.globalProperties.$locale = locale;
        app.provide('locale', locale);
    }
}

export function useLocale() {
    return inject('locale');
}